<template>
  <v-row>
    <v-col cols="12" lg="12">
      <Aggregate
        title="Energy Storage Systems"
        v-model="computedInput.storage"
        :error-messages="$utils.getIfExists(errors, ['input', 'storage'])"
        :ItemConstructor="storageFactory"
        :headers="headers"
        v-bind="$attrs"
        copy-btn
        dense
        @change="$emit('change')"
      >
        <template #item.active="{ item }">
          <v-switch
            v-model="item.active"
            dense
            class="mt-0"
            hide-details
            @change="$emit('change')"
          ></v-switch>
        </template>
        <template #item.bus_id="{ item }">
          <v-select
            clearable
            v-model="item.bus_id"
            :items="nodeIndex"
            label="Connected Node"
            single-line
            dense
            @change="$emit('change')"
          >
            <template #selection="data">
              {{ data.item.name }}
              <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
            </template>
            <template #item="data">
              {{ data.item.name }}
              <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
            </template>
          </v-select>
        </template>
        <template #item.source="{ item, errors, allowEdit }">
          <v-select
            v-model="item.source"
            :items="sources"
            :error-messages="$utils.getIfExists(errors, ['source', 'messages'])"
            :disabled="!allowEdit || item.noDelete"
            label="Energy Storage System"
            dense
            @change="$emit('change')"
          ></v-select>
        </template>
        <template #item.capacity="{ item }">
          <td>{{ item.power_capacity[0] }} MW</td>
        </template>
        <template #default="{ item, errors }">
          <v-card class="pa-2 ma-2" flat>
            <v-card-text>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">General</p>
                <InputGeo
                  v-model="item.coordinates"
                  label="Coordinates"
                  map-height="150"
                  nullable
                  switch-text="Set coordinates"
                  icon="storage"
                  :initial-coordinates="nodeCoordinates(item.bus_id)"
                  @change="$emit('change')"
                ></InputGeo>
                <v-switch
                  v-model="item.expansion_candidate"
                  :error-messages="$utils.getIfExists(errors, ['expansion_candidate', 'messages'])"
                  label="Expansion Candidate"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <v-switch
                  v-model="item.include_elcc"
                  :label="schema.include_elcc.title"
                  :hint="schema.include_elcc.tooltip"
                  persistent-hint
                  class="mb-4"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <InputTextField
                  v-model.number="item.technical_life"
                  :label="schema.technical_life.title"
                  :hint="schema.technical_life.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['technical_life', 'messages'])"
                  :suffix="schema.technical_life.unit"
                  type="number"
                  @change="$emit('change')"
                ></InputTextField>
                <InputTextField
                  v-model.number="item.start_year"
                  :label="schema.start_year.title"
                  :suffix="schema.start_year.unit"
                  :hint="schema.start_year.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['start_year', 'messages'])"
                  type="number"
                  class="my-4"
                  @change="$emit('change')"
                ></InputTextField>
                <InputTextField
                  v-model.number="item.charging_efficiency"
                  :label="schema.charging_efficiency.title"
                  :hint="schema.charging_efficiency.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['charging_efficiency', 'messages'])"
                  :suffix="schema.charging_efficiency.unit"
                  type="number"
                  class="my-4"
                  @change="$emit('change')"
                ></InputTextField>
                <InputTextField
                  v-model.number="item.discharging_efficiency"
                  :label="schema.discharging_efficiency.title"
                  :hint="schema.discharging_efficiency.tooltip"
                  :error-messages="
                    $utils.getIfExists(errors, ['discharging_efficiency', 'messages'])
                  "
                  :suffix="schema.discharging_efficiency.unit"
                  type="number"
                  class="my-4"
                  @change="$emit('change')"
                ></InputTextField>
                <InputTextField
                  v-model.number="item.ramp_rate"
                  :label="schema.ramp_rate.title"
                  :hint="schema.ramp_rate.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['ramp_rate', 'messages'])"
                  :suffix="schema.ramp_rate.unit"
                  type="number"
                  class="my-4"
                  @change="$emit('change')"
                ></InputTextField>
                <InputTextField
                  v-model.number="item.soc_min"
                  :label="schema.soc_min.title"
                  :hint="schema.soc_min.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['soc_min', 'messages'])"
                  :suffix="schema.soc_min.unit"
                  type="number"
                  class="my-4"
                  @change="$emit('change')"
                ></InputTextField>
                <InputTextField
                  v-model.number="item.soc_max"
                  :label="schema.soc_max.title"
                  :hint="schema.soc_max.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['soc_max', 'messages'])"
                  :suffix="schema.soc_max.unit"
                  type="number"
                  class="my-4"
                  @change="$emit('change')"
                ></InputTextField>
                <v-row>
                  <v-col md="5">
                    <v-switch
                      v-model="item.duration"
                      :label="schema.duration.title"
                      :hint="schema.duration.tooltip"
                      :error-messages="$utils.getIfExists(errors, ['duration', 'messages'])"
                      class="pt-3 mt-1 pr-2"
                      :true-value="-1"
                      false-value="0"
                      dense
                    ></v-switch>
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-if="item.duration != -1"
                      v-model.number="item.duration"
                      :error-messages="$utils.getIfExists(errors, ['duration', 'messages'])"
                      :hint="schema.duration.tooltip"
                      :suffix="schema.duration.unit"
                      type="number"
                      @change="$emit('change')"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
                <InputProfile
                  v-model="item.capacity_fade"
                  :label="schema.capacity_fade.title"
                  :hint="schema.capacity_fade.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['capacity_fade'])"
                  profile-type="CAPACITY_FADE"
                  :useHeaders="['cycle_limit', 'capacity_fade']"
                  nullable
                  @change="$emit('change')"
                ></InputProfile>
                <InputYearly
                  v-model="item.estimated_cycles"
                  :error-messages="$utils.getIfExists(errors, ['estimated_cycles'])"
                  label="Estimated Number of Cycles"
                  suffix="cycles"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  @change="$emit('change')"
                ></InputYearly>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Metrics</p>
                <v-switch
                  v-model="item.LCOE_Calculation"
                  :label="schema.LCOE_calculation.title"
                  :hint="schema.LCOE_calculation.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['source', 'messages'])"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Financial</p>
                <InputTextField
                  v-model.number="item.economic_life"
                  :label="schema.economic_life.title"
                  :hint="schema.economic_life.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['economic_life', 'messages'])"
                  :suffix="schema.economic_life.unit"
                  type="number"
                  class="my-4"
                  @change="$emit('change')"
                ></InputTextField>
                <InputTextField
                  v-model.number="item.discount"
                  :label="schema.discount.title"
                  :hint="schema.discount.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['discount', 'messages'])"
                  :placeholder="
                    input.discount && input.discount != null ? input.discount.toString() : ''
                  "
                  :suffix="schema.discount.unit"
                  type="number"
                  class="my-4"
                  @change="$emit('change')"
                ></InputTextField>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Costs</p>
                <InputYearly
                  v-model="item.investment"
                  :label="schema.investment.title"
                  :hint="schema.investment.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['investment'])"
                  :suffix="schema.investment.unit"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="schema.investment.items.default"
                  @change="$emit('change')"
                ></InputYearly>
                <v-switch
                  v-model="item.ppa.active"
                  :label="schema.ppa.title"
                  :hint="schema.ppa.tooltip"
                  persistent-hint
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <InputYearlyProfile
                  v-if="item.ppa.active"
                  v-model="item.ppa.charging"
                  object-value
                  fill
                  toggle-items-type
                  :label="schema.ppa.properties.charging.title"
                  :hint="schema.ppa.properties.charging.tooltip"
                  suffix="$/MWh"
                  :num_years="num_years"
                  :profile-type="schema.ppa.properties.charging.profileType"
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <InputYearlyProfile
                  v-if="item.ppa.active"
                  v-model="item.ppa.discharging"
                  object-value
                  fill
                  toggle-items-type
                  :label="schema.ppa.properties.discharging.title"
                  :hint="schema.ppa.properties.discharging.tooltip"
                  suffix="$/MWh"
                  :num_years="num_years"
                  :profile-type="schema.ppa.properties.discharging.profileType"
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <InputYearlyProfile
                  v-if="item.ppa.active"
                  v-model="item.ppa.capacity"
                  object-value
                  fill
                  toggle-items-type
                  :label="schema.ppa.properties.capacity.title"
                  :hint="schema.ppa.properties.capacity.tooltip"
                  suffix="$/MW-month"
                  :num_years="num_years"
                  :profile-type="schema.ppa.properties.capacity.profileType"
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <InputYearly
                  v-model="item.investment_energy"
                  :label="schema.investment_energy.title"
                  :hint="schema.investment_energy.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['investment_energy'])"
                  :suffix="schema.investment.unit"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="schema.investment_energy.items.default"
                  @change="$emit('change')"
                ></InputYearly>
                <InputYearly
                  v-model="item.vom"
                  :label="schema.vom.title"
                  :hint="schema.vom.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['vom'])"
                  :suffix="schema.vom.unit"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="schema.vom.items.default"
                  @change="$emit('change')"
                ></InputYearly>
                <InputTextField
                  v-model.number="item.fom"
                  :label="schema.fom.title"
                  :hint="schema.fom.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['fom', 'messages'])"
                  :suffix="schema.fom.unit"
                  type="number"
                  :min="schema.fom.minimum"
                  @change="$emit('change')"
                ></InputTextField>
                <InputTextField
                  v-model.number="item.eol_cost"
                  :label="schema.eol_cost.title"
                  :suffix="schema.eol_cost.unit"
                  type="number"
                  :min="schema.eol_cost.minimum"
                >
                </InputTextField>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Services</p>
                <InputYearly
                  v-model="item.energy_arbitrage"
                  :label="schema.energy_arbitrage.title"
                  :hint="schema.energy_arbitrage.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['energy_arbitrage'])"
                  :suffix="schema.energy_arbitrage.unit"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="schema.energy_arbitrage.items.default"
                  :min="schema.energy_arbitrage.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <InputYearly
                  v-model="item.marginal_cost"
                  :label="schema.marginal_cost.title"
                  :hint="schema.marginal_cost.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['marginal_cost'])"
                  :suffix="schema.marginal_cost.unit"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="schema.marginal_cost.items.default"
                  :min="schema.marginal_cost.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Capacity</p>
                <InputYearly
                  v-model="item.maximum_build"
                  :label="schema.maximum_build.title"
                  :hint="schema.maximum_build.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['maximum_build'])"
                  :suffix="schema.maximum_build.unit"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="schema.maximum_build.items.default"
                  :min="schema.maximum_build.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <InputYearly
                  v-model="item.power_capacity"
                  :label="schema.power_capacity.title"
                  :hint="schema.power_capacity.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['power_capacity'])"
                  :suffix="schema.power_capacity.unit"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="schema.power_capacity.items.default"
                  :min="schema.power_capacity.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <InputYearly
                  v-model="item.energy_capacity"
                  :label="schema.energy_capacity.title"
                  :hint="schema.energy_capacity.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['energy_capacity'])"
                  :suffix="schema.energy_capacity.unit"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="schema.energy_capacity.items.default"
                  :min="schema.energy_capacity.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <InputTextField
                  v-model.number="item.firm"
                  :label="schema.firm.title"
                  :hint="schema.firm.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['firm', 'messages'])"
                  :suffix="schema.firm.unit"
                  type="number"
                  @change="$emit('change')"
                ></InputTextField>
                <!-- <v-btn
                  small
                  :outlined="!showAdvanced.capacity"
                  color="primary"
                  class="mb-2 mt-4"
                  @click="showAdvanced.capacity = !showAdvanced.capacity"
                  >Advanced...</v-btn
                > -->
                <InputYearly
                  v-model="item.max_charge_power"
                  :label="schema.max_charge_power.title"
                  :hint="schema.max_charge_power.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['max_charge_power'])"
                  :suffix="schema.max_charge_power.unit"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="schema.max_charge_power.items.default"
                  :min="schema.max_charge_power.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <InputYearly
                  v-model="item.max_discharge_power"
                  :label="schema.max_discharge_power.title"
                  :hint="schema.max_discharge_power.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['max_discharge_power'])"
                  :suffix="schema.max_discharge_power.unit"
                  type="number"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="schema.max_discharge_power.items.default"
                  :min="schema.max_discharge_power.items.minimum"
                  @change="$emit('change')"
                ></InputYearly>
                <v-divider class="mt-4"></v-divider>
              </v-card>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Operation</p>
                <InputToggleParam
                  v-model="item.operating_status"
                  :label="schema.operating_status.properties.active.title"
                  :hint="schema.operating_status.properties.active.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['operating_status'])"
                  @change="$emit('change')"
                  hide-divider
                >
                  <template v-slot="{ param, errors }">
                    <InputProfile
                      v-model="param.id"
                      :hint="schema.operating_status.then.properties.param.properties.id.tooltip"
                      :error-messages="$utils.getIfExists(errors, ['id'])"
                      profile-type="DAILY_OPERATING_STATUS"
                      @change="$emit('change')"
                    ></InputProfile>
                  </template>
                </InputToggleParam>
                <InputToggleParam
                  v-model="item.discharge_profile"
                  :label="schema.discharge_profile.properties.active.title"
                  :hint="schema.discharge_profile.properties.active.tooltip"
                  :error-messages="$utils.getIfExists(errors, ['discharge_profile'])"
                  @change="$emit('change')"
                  hide-divider
                >
                  <template #below="{ param, errors }">
                    <v-col class="grow">
                      <InputYearlyProfile
                        v-model="param.profiles"
                        :hint="
                          schema.discharge_profile.properties.param.properties.profiles.tooltip
                        "
                        :error-messages="$utils.getIfExists(errors, ['profiles'])"
                        :num_years="num_years"
                        fill
                        profile-type="DISCHARGE"
                        @change="$emit('change')"
                      ></InputYearlyProfile>
                    </v-col>
                  </template>
                </InputToggleParam>
                <v-divider class="mt-4"></v-divider>
              </v-card>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-bold">Power Flow Parameters</p>
                <InputYearlyProfile
                  v-model="item.active_power"
                  :label="schema.active_power.title"
                  :suffix="schema.active_power.unit"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  :fill-value="schema.active_power.items.default.value"
                  object-value
                  toggle-items-type
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <InputYearlyProfile
                  v-model="item.reactive_power"
                  :label="schema.reactive_power.title"
                  :suffix="schema.reactive_power.unit"
                  :num_years="num_years"
                  :year_start="startYear"
                  fill
                  object-value
                  toggle-items-type
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <InputTextField
                  v-model.number="item.scaling"
                  :label="schema.scaling.title"
                  :suffix="schema.scaling.unit"
                  type="number"
                  @change="$emit('change')"
                ></InputTextField>
                <InputTextField
                  v-model.number="item.max_charge_level"
                  :label="schema.max_charge_level.title"
                  :suffix="schema.max_charge_level.unit"
                  type="number"
                  @change="$emit('change')"
                ></InputTextField>
                <v-divider class="mt-4"></v-divider>
              </v-card>
            </v-card-text>
          </v-card>
        </template>
      </Aggregate>
    </v-col>
  </v-row>
</template>

<script>
import { CaseModel, schemas } from "data-dictionary";

export default {
  name: "SystemStorage",
  components: {
    Aggregate: () => import("@/components/Aggregate.vue"),
    InputGeo: () => import("@/components/InputGeo.vue"),
    InputYearly: () => import("@/components/InputYearly.vue"),
    InputTextField: () => import("@/components/inputs/InputTextField.vue"),
    InputToggleParam: () => import("@/components/InputToggleParam.vue"),
    InputProfile: () => import("@/components/InputProfile.vue"),
    InputYearlyProfile: () => import("@/components/InputYearlyProfile.vue"),
  },
  props: {
    num_years: {
      type: Number,
    },
    input: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: false,
    },
    startYear: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      headers: [
        { text: "Active", value: "active", width: 30 },
        { text: "Name", value: "name" },
        { text: "Source", value: "source" },
        { text: "Connected Node", value: "bus_id" },
        { text: "Capacity", value: "capacity" },
      ],
      sources: CaseModel.Storage.SOURCES,
      schema: schemas.filter((v) => v.$id === "storage.schema.json")[0].properties,
      showAdvanced: {
        capacity: false,
      },
    };
  },
  computed: {
    computedInput: {
      get() {
        return this.input;
      },
      set(val) {
        this.$emit("update:input", val);
        this.$emit("change");
      },
    },
    nodeIndex() {
      return this.computedInput.nodes
        ? this.computedInput.nodes.map((el) => ({
            name: el.name,
            value: el.id,
          }))
        : [];
    },
  },

  methods: {
    storageFactory: CaseModel.Storage,
    nodeCoordinates(busId) {
      return busId ? this.input.nodes.find((v) => v.id === busId).coordinates : undefined;
    },
  },
};
</script>

<style></style>
